import { useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertCircle, AlertTriangle, Wrench } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { SEO } from "@/components/SEO";
import {
  findComboBySlug,
  ALL_COMBOS,
} from "@/data/panneVilleData";
import { buildPanneVilleSchema } from "@/data/panneVilleSchema";
import { PanneVilleHero, PanneVilleAccess } from "@/components/pannevilles/PanneVilleHero";
import {
  PanneVilleFaq,
  PanneVilleInternalLinks,
} from "@/components/pannevilles/PanneVilleSections";
import {
  PanneDiagnostic,
  PanneSolution,
} from "@/components/pannes/PanneDiagnostic";
import {
  PannePricing,
  PanneCta,
} from "@/components/pannes/PanneSections";

export default function PanneVillePage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const combo = findComboBySlug(slug);

  if (!combo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center px-4">
          <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">Page introuvable</h1>
          <p className="text-slate-600 mb-6">Cette combinaison panne/ville n&apos;existe pas.</p>
          <Link to="/reparations-locales">
            <Button>Voir toutes les combinaisons</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { panne, city, h1, metaTitle, metaDescription } = combo;
  const Icon = LucideIcons[panne.icon] || Wrench;
  const schema = buildPanneVilleSchema(combo);
  const keywords = `${panne.keyword.replace("Niort", city.name)}, ${combo.panneShort.toLowerCase()} ${city.name}, ${panne.category} ${city.name}, réparation ${city.name}, Mediastore ${city.name}, réparateur Apple ${city.name}, ${combo.panneShort.toLowerCase()} ${city.postalCode}`;

  // Trouve 5 autres villes pour la même panne (maillage interne)
  const otherCombos = ALL_COMBOS
    .filter((c) => c.panne.slug === panne.slug && c.city.slug !== city.slug)
    .slice(0, 5);

  const breadcrumbs = [
    { name: "Accueil", path: "/" },
    { name: "Réparations locales", path: "/reparations-locales" },
    { name: city.name, path: `/reparation-${city.slug}` },
    { name: combo.panneShort, path: `/${combo.slug}` },
  ];

  return (
    <div className="min-h-screen" data-testid="panne-ville-page">
      <SEO
        title={metaTitle}
        description={metaDescription}
        keywords={keywords}
        path={`/${combo.slug}`}
        schema={schema}
        breadcrumbs={breadcrumbs}
      />

      <PanneVilleHero combo={combo} Icon={Icon} />

      {/* Urgent tip contextualisé pour les habitants de la ville */}
      <section className="bg-amber-50 border-y-2 border-amber-200">
        <div className="container-custom max-w-5xl py-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="h-7 w-7 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-bold text-amber-900 mb-1">
                Habitants de {city.name} — à faire IMMÉDIATEMENT
              </h2>
              <p className="text-amber-900 leading-relaxed">{panne.urgentTip}</p>
            </div>
          </div>
        </div>
      </section>

      <PanneVilleAccess city={city} />
      <PanneDiagnostic symptoms={panne.symptoms} causes={panne.causes} />
      <PanneSolution solution={panne.solution} durationLabel={panne.durationLabel} />
      <PannePricing titleH1={panne.titleH1} pricing={panne.pricing} />
      <PanneVilleFaq combo={combo} />
      <PanneVilleInternalLinks combo={combo} otherCombos={otherCombos} />
      <PanneCta />
    </div>
  );
}
